import React, { useEffect, useRef, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";

function Australiaskilledmigrationvisaimport() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 5);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-wrap">
      <div className="mb-20 mt-20 flex min-h-screen">
        <div
          ref={titleRef}
          className="sticky top-20 hidden h-screen w-1/4 overflow-y-auto bg-gray-100 p-4 md:block"
        >
          <ul className="space-y-2">
            <li
              onClick={() => scrollToSection(0)}
              className={`cursor-pointer p-8 ${
                activeIndex === 0
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Australia Skilled Migration Visa Overview
            </li>
            <li
              onClick={() => scrollToSection(1)}
              className={`cursor-pointer p-8 ${
                activeIndex === 1
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Eligibility Requirements for the Australia Skilled Migration Visa
            </li>
            <li
              onClick={() => scrollToSection(2)}
              className={`cursor-pointer p-8 ${
                activeIndex === 2
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Required Documents for the Australia Skilled Migration Visa Application
            </li>
            <li
              onClick={() => scrollToSection(3)}
              className={`cursor-pointer p-8 ${
                activeIndex === 3
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Application Process for the Australia Skilled Migration Visa
            </li>
          </ul>
        </div>

        <div
          ref={contentRef}
          className="mb-10 h-screen overflow-y-auto p-4 md:w-3/4 lg:w-3/4 xl:w-3/4"
        >
          <div ref={(el) => (sectionRefs.current[0] = el)}>
            <p className="text-xl font-bold text-[#011473]">
              Australia Skilled Migration Visa Overview
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              The Australia Skilled Migration Visa is designed to allow skilled professionals to permanently live and work in Australia. This visa is aimed at individuals with expertise and qualifications that are in demand within the Australian workforce. There are various subclasses within this visa category, including:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Subclass 189: Skilled Independent Visa</li>
              <li>Subclass 190: Skilled Nominated Visa</li>
              <li>Subclass 491: Skilled Work Regional (Provisional) Visa</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Eligibility Requirements for the Australia Skilled Migration Visa
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              To qualify for the Australia Skilled Migration Visa, you must fulfill the following criteria:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Age: Applicants must be under 45 years of age at the time of receiving an invitation.</li>
              <li>English Language Proficiency: You must prove your English skills through accepted tests such as IELTS, TOEFL, or PTE Academic.</li>
              <li>Skills Assessment: A positive skills assessment must be obtained from a recognized assessing authority relevant to your occupation.</li>
              <li>Occupation: Your occupation must be listed on the relevant skilled occupation list.</li>
              <li>Points System: You need to achieve a minimum score of 65 points on the points test, which evaluates factors such as age, qualifications, work experience and language proficiency.</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Required Documents for the Australia Skilled Migration Visa Application
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Passport: Your passport should be valid for at least six months from your intended date of arrival in Australia.</li>
              <li>Skills Assessment: A positive skills assessment from the relevant authority based on your occupation.</li>
              <li>English Proficiency: Results from recognized language proficiency tests (e.g., IELTS, TOEFL, or PTE).</li>
              <li>Expression of Interest (EOI): Submit an Expression of Interest via SkillSelect.</li>
              <li>Academic Qualifications: Certified copies of your educational certificates, including degrees and transcripts.</li>
              <li>Work Experience: Documentation of your employment history, including references, job descriptions and payslips.</li>
              <li>Health and Character Documents:
                <ul className="mt-2 list-inside list-disc pl-6 text-[#2C3F69]">
                  <li>Health Check: Must be performed by an approved medical practitioner.</li>
                  <li>Police Clearance: Certificates from each country you've lived in for more than 12 months in the last 10 years.</li>
                </ul>
              </li>
              <li>Identity Proof: Documents verifying your identity, such as a birth certificate or national identification card.</li>
              <li>Family Relationship Documents: If applying with dependents, provide documents like marriage certificates, birth certificates, or joint financial records.</li>
              <li>Proof of Financial Stability: Evidence that you can financially support yourself and your dependents in Australia.</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Application Process for the Australia Skilled Migration Visa
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Eligibility Assessment: Ensure that you meet all the necessary criteria for the visa.</li>
              <li>Submit an Expression of Interest (EOI): Complete and submit your EOI through SkillSelect.</li>
              <li>Invitation to Apply: If your EOI is successful, you will receive an invitation to apply for the visa.</li>
              <li>Document Collection: Gather and prepare all required documents for submission.</li>
              <li>Visa Application Submission: Submit your complete application online.</li>
              <li>Await Visa Outcome: Wait for the Department of Home Affairs to process and provide a decision on your application.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Australiaskilledmigrationvisaimport;
